import { Component, input, output, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-timepicker',
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimepickerComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-1.5">
      @if (label()) {
        <label 
          [for]="id() || name()"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <div class="relative">
        <input
          type="time"
          [id]="id() || name()"
          [name]="name()"
          [placeholder]="placeholder()"
          [disabled]="disabled()"
          [readonly]="readonly()"
          [min]="min()"
          [max]="max()"
          [step]="step()"
          [class]="inputClasses"
          [value]="value()"
          (input)="onInput($event)"
          (blur)="onBlur()"
          (focus)="onFocus()" />
      </div>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium flex items-center gap-1">
          <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class TimepickerComponent implements ControlValueAccessor {
  label = input<string>('');
  placeholder = input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  min = input<string>('');
  max = input<string>('');
  step = input<number>(60);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  size = input<ComponentSize>('md');

  valueChange = output<string>();

  value = signal<string>('');
  touched = signal(false);

  private onChange = (value: string) => {};
  private onTouched = () => {};

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.value.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);
    this.markAsTouched();
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouched();
  }

  onFocus(): void {
    // Focus handling
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  get inputClasses(): string {
    const classes = [
      'w-full',
      'px-4',
      'py-3',
      'border',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'placeholder:text-gray-400',
      'dark:placeholder:text-gray-500'
    ];

    const sizeMap = {
      xs: 'px-2 py-1 text-xs pr-8',
      sm: 'px-3 py-2 text-sm pr-10',
      md: 'px-4 py-3 text-base pr-12',
      lg: 'px-5 py-4 text-lg pr-14',
      xl: 'px-6 py-5 text-xl pr-16'
    };
    classes.push(sizeMap[this.size()]);

    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100', 'dark:focus:ring-red-900/30');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');
    }

    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-900', 'dark:text-gray-100');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }
}

